import { useState, useCallback, MutableRefObject } from 'react';
import { IAssistantScript, PlaybackPolicy, ProgressEntry } from '@interfaces/assistantScript';
import { IUser } from '@interfaces/user';
import { PollyLanguage } from '@shared/interfaces/polly';
import { ScriptNode, LinearScriptPlayer } from './scriptPlayer/linearScriptPlayer';
import { ScriptNodeJSON } from './scriptPlayer/intefraces';
import { log } from './Debug';
import { getCurrentRegion } from './globals';
import { isAITrigger } from './providerUtils';
import { regionNameToCamelCase } from './Regions';
import { selectVoiceForLanguage } from '@constants/polly';
import { toPollyLanguageCode } from '@constants/language';
import { replaceAssistantPlaceholders } from '@shared/utils/assistantScript';
import { GetEffectiveAiSettingsFn } from './useAssistantScripts';

export type PlayBlockAudioFn = (
  text: string,
  engine?: string,
  voiceId?: string,
  language?: string,
) => Promise<void>;

export type StartFlowOptions = {
  scriptId?: string;
  force?: boolean;
  editorMode?: boolean;
  forceUiClick?: boolean;
  /** 1-based `step-N` node id to start at, overriding the default resume logic. */
  startStep?: number;
  /**
   * Skip playback-policy gating (once_session/once_user/first_time) and the
   * ui_click "Take a tour" confirm-button gate — used by the visited-areas
   * fast-travel actions (Resume/Pick a step/Replay), which are an explicit
   * user choice to revisit content regardless of trigger type or play history.
   */
  bypassPlaybackPolicy?: boolean;
};

export type StartFlowFn = (
  trigger: string,
  options?: StartFlowOptions,
) => void;

export type CbOnCompletedStepFn = (
  trigger: string,
  stepNumber: number,
  step: string,
) => void;

export type StartAudioPrerenderFn = (
  currentArea: string,
  startStep: number,
) => Promise<void>;

export type UseScriptFlowParams = {
  scriptsRef: MutableRefObject<IAssistantScript[]>;
  progressState: MutableRefObject<string>;
  sessionProgressRef: MutableRefObject<Record<string, ProgressEntry[]>>;
  linearScriptPlayerRef: MutableRefObject<LinearScriptPlayer | undefined>;
  playBlockAudioRef: MutableRefObject<PlayBlockAudioFn>;
  cbOnCompletedStepRef: MutableRefObject<CbOnCompletedStepFn>;
  resetAssistantUIRef: MutableRefObject<() => void>;
  mytaverseUserRef?: MutableRefObject<IUser | null | undefined>;
  mytaverseEventRef: MutableRefObject<any>;
  userNameRef: MutableRefObject<string | undefined>;
  selectedLanguageRef: MutableRefObject<string>;
  preRenderedAudioRef: MutableRefObject<
    {
      text: string;
      audio: null | ArrayBuffer;
      voiceId: string;
      engine: string;
      language: string;
      area: string;
      index: number;
    }[]
  >;
  isAudioPlaybackActiveRef: MutableRefObject<boolean>;
  openRef: MutableRefObject<boolean>;
  setOpen: (open: boolean) => void;
  startAudioPrerenderRef: MutableRefObject<StartAudioPrerenderFn>;
  startFlowRef: MutableRefObject<StartFlowFn>;
  getEffectiveAiSettings: GetEffectiveAiSettingsFn;
};

export const useScriptFlow = ({
  scriptsRef,
  progressState,
  sessionProgressRef,
  linearScriptPlayerRef,
  playBlockAudioRef,
  cbOnCompletedStepRef,
  resetAssistantUIRef,
  mytaverseUserRef,
  mytaverseEventRef,
  userNameRef,
  selectedLanguageRef,
  preRenderedAudioRef,
  isAudioPlaybackActiveRef,
  openRef,
  setOpen,
  startAudioPrerenderRef,
  startFlowRef,
  getEffectiveAiSettings,
}: UseScriptFlowParams) => {
  const [isScriptCompleted, setIsScriptCompleted] = useState(false);
  const [isScriptTransitioning, setIsScriptTransitioning] = useState(false);
  const [canUserSkip, setCanUserSkip] = useState(false);
  const [pendingScriptStart, setPendingScriptStart] = useState<{
    step: number;
    area: string;
    title: string;
  } | null>(null);
  const [isScriptQuizActive, setIsScriptQuizActive] = useState(false);

  const startFlow: StartFlowFn = useCallback(
    (scriptTrigger, options) => {
      const {
        scriptId,
        force,
        editorMode,
        forceUiClick,
        startStep,
        bypassPlaybackPolicy,
      } = options ?? {};
      setIsScriptCompleted(false);

      const currentScripts = scriptsRef.current;
      const script = scriptId
        ? currentScripts.find((x) => x.scriptId === scriptId)
        : currentScripts.find((x) => {
            const triggers = Array.isArray(x.scriptTrigger)
              ? x.scriptTrigger
              : [x.scriptTrigger];
            return triggers.some((t) => {
              const normalizedTrigger =
                t.includes('_') && !t.startsWith('AITrigger_')
                  ? regionNameToCamelCase(t)
                  : t;
              return normalizedTrigger === scriptTrigger || t === scriptTrigger;
            });
          });
      setCanUserSkip(script?.canUserSkip ?? false);
      const isCustomSpot = script?.triggerType === 'custom_spot';
      const isUiClick =
        !bypassPlaybackPolicy &&
        (forceUiClick || script?.triggerType === 'ui_click' || isCustomSpot);
      const scriptData = script?.scriptData;

      let parsedScriptData: ScriptNodeJSON[] = [];

      if (scriptData) {
        try {
          parsedScriptData = JSON.parse(scriptData);
          log('whole script', JSON.stringify(parsedScriptData, null, 2));
        } catch (error) {
          log('makePlayerFromScriptData error', error);
        }
      }

      if (!parsedScriptData.length) {
        return;
      }

      const currentArea = scriptTrigger ? scriptTrigger : getCurrentRegion();

      const playerState = linearScriptPlayerRef.current?.getCurrentState();
      if (playerState?.isRunning && playerState?.name === currentArea) {
        log('script player is already running');
        return;
      }

      const currentProgressState = JSON.parse(progressState.current || '{}');

      log('currentProgressState player', {
        currentProgressState,
        currentAreaProgress:
          currentProgressState?.[currentArea]?.at(-1)?.[0] ?? 0,
        currentAreaTotalSteps: parsedScriptData.length,
      });

      const defaultPlaybackPolicy = isAITrigger(script?.scriptTrigger ?? '')
        ? PlaybackPolicy.ALWAYS
        : PlaybackPolicy.ONCE_SESSION;
      const playbackPolicy = script?.playbackPolicy ?? defaultPlaybackPolicy;
      log('script playbackPolicy=', playbackPolicy);

      if (parsedScriptData.length > 0 && !bypassPlaybackPolicy) {
        const lastStep = parsedScriptData.length - 1;
        const isCompletedIn = (state: Record<string, ProgressEntry[]>) =>
          !!state[currentArea] &&
          (state[currentArea]?.at(-1)?.[0] ?? 0) >= lastStep;

        if (force) {
          delete sessionProgressRef.current[currentArea];
        }

        let shouldSkip = false;
        if (playbackPolicy === PlaybackPolicy.ONCE_SESSION) {
          if (isCompletedIn(sessionProgressRef.current)) {
            log('script already played this session (once_session)');
            shouldSkip = true;
          }
        } else if (playbackPolicy === PlaybackPolicy.ONCE_USER) {
          if (isCompletedIn(currentProgressState)) {
            log('script already completed (once_user)');
            shouldSkip = true;
          }
        } else if (playbackPolicy === PlaybackPolicy.FIRST_TIME) {
          if (!mytaverseUserRef?.current?.isFirstTimeUser) {
            log('script skipped — not a first-time user (first_time)');
            shouldSkip = true;
          } else if (isCompletedIn(sessionProgressRef.current)) {
            log('script already played this session (first_time)');
            shouldSkip = true;
          }
        }

        if (shouldSkip) {
          if (!isCustomSpot && !forceUiClick) {
            if (!isAudioPlaybackActiveRef.current || isUiClick) {
              resetAssistantUIRef.current();
            }
            return;
          }
          // custom_spot and forceUiClick always show the button regardless of play history
        }
      }

      const effectiveAiSettings = getEffectiveAiSettings();

      const langKey = toPollyLanguageCode(
        selectedLanguageRef.current,
      ) as PollyLanguage;

      // In editor mode all languages are allowed, bypassing the event's language
      // restrictions so authors can preview any language they have configured.
      // Otherwise a language is eligible only when the event's languages list
      // either is absent (unrestricted) or explicitly includes this language.
      const allowedByEvent =
        editorMode ||
        !effectiveAiSettings.languages?.length ||
        effectiveAiSettings.languages.includes(langKey);

      const allStepsTranslated =
        allowedByEvent &&
        parsedScriptData.every((node) => !!node.translations?.[langKey]);

      // True when the selected language is the script's base language (always
      // playable via node.text) or every step has a translation for that language.
      const translationComplete =
        langKey === effectiveAiSettings.baseLanguage ||
        parsedScriptData.every((node) => !!node.translations?.[langKey]);

      const missingTranslationSteps =
        allowedByEvent && !allStepsTranslated
          ? parsedScriptData
              .map((node, i) => (!node.translations?.[langKey] ? i + 1 : null))
              .filter(Boolean)
          : [];

      const allTranslationKeys = [
        ...new Set(
          parsedScriptData.flatMap((node) =>
            Object.keys(node.translations ?? {}),
          ),
        ),
      ];
      const fullyTranslatedLanguages = [
        effectiveAiSettings.baseLanguage,
        ...allTranslationKeys.filter((lang) =>
          parsedScriptData.every((node) => !!node.translations?.[lang]),
        ),
      ];

      const incompleteTranslations = allTranslationKeys
        .filter((lang) => !fullyTranslatedLanguages.includes(lang))
        .reduce<Record<string, number[]>>((acc, lang) => {
          acc[lang] = parsedScriptData
            .map((node, i) => (!node.translations?.[lang] ? i + 1 : null))
            .filter((n): n is number => n !== null);
          return acc;
        }, {});

      log('[lang] building nodes', {
        selectedLanguage: selectedLanguageRef.current,
        langKey,
        baseLanguage: effectiveAiSettings.baseLanguage,
        allowedLanguages: effectiveAiSettings.languages ?? 'unrestricted',
        allowedByEvent,
        allStepsTranslated,
        translationComplete,
        missingTranslationSteps,
        fullyTranslatedLanguages,
        incompleteTranslations,
        totalSteps: parsedScriptData.length,
        currentArea,
      });

      if (!editorMode && !translationComplete) {
        log(
          `[lang] skipping — script not fully translated into "${langKey}"`,
          { missingTranslationSteps },
        );
        return;
      }

      const nodes: ScriptNode[] = parsedScriptData.map((node, index) => {
        let advanceOnAudioEndFallback = false;
        const originalText = node.text;

        let nodeLanguage: PollyLanguage = effectiveAiSettings.baseLanguage;
        let nodeEngine = effectiveAiSettings.engine;
        let nodeVoiceId = effectiveAiSettings.voiceId;

        if (allStepsTranslated) {
          const selected = selectVoiceForLanguage(
            langKey,
            effectiveAiSettings.voicesByLanguage,
          );
          node.text = node.translations![langKey];
          nodeLanguage = langKey;
          nodeEngine = selected.engine;
          nodeVoiceId = selected.voiceId;
          log(`[lang] step-${index + 1}: using translation for "${langKey}"`, {
            originalText,
            translatedText: node.text,
            pollyLanguage: nodeLanguage,
            engine: nodeEngine,
            voiceId: nodeVoiceId,
          });
        } else {
          const reason = !allowedByEvent
            ? `"${langKey}" not in event languages list`
            : `script has untranslated steps (${missingTranslationSteps.join(
                ', ',
              )})`;
          const baseTranslation =
            node.translations?.[effectiveAiSettings.baseLanguage];
          if (baseTranslation !== undefined) {
            node.text = baseTranslation;
          }
          log(
            `[lang] step-${index + 1}: falling back to baseLanguage (${
              effectiveAiSettings.baseLanguage
            }) — ${reason}`,
            {
              originalText,
              pollyLanguage: nodeLanguage,
            },
          );
        }
        node.text = replaceAssistantPlaceholders(
          node.text,
          userNameRef.current,
          mytaverseEventRef.current.currentEvent?.name,
        );

        // Any step with no configured way to advance (no button, no
        // click-anywhere, no allowed keys) would otherwise leave playback
        // stuck in waitForAdvance() forever once its audio ends.
        if (
          !node.awaitButtonNext &&
          node.questionary?.type !== 'multiple_choice' &&
          !node.advanceOnAudioEnd &&
          !node.allowedKeys?.length &&
          !node.enableClickAnywhere
        ) {
          advanceOnAudioEndFallback = true;
        }

        if (
          !preRenderedAudioRef.current.find(
            (x) =>
              x.text === node.text &&
              x.engine === nodeEngine &&
              x.voiceId === nodeVoiceId &&
              x.language === nodeLanguage,
          )
        ) {
          preRenderedAudioRef.current.push({
            area: currentArea,
            index: index + 1,
            audio: null,
            text: node.text,
            engine: nodeEngine,
            voiceId: nodeVoiceId,
            language: nodeLanguage,
          });
        }

        return {
          id: `step-${index + 1}`,
          play: () => {
            cbOnCompletedStepRef.current(
              currentArea,
              index,
              `step-${index + 1}`,
            );
            return playBlockAudioRef.current(
              node.text,
              nodeEngine,
              nodeVoiceId,
              nodeLanguage,
            );
          },
          awaitButtonNext:
            node.questionary?.type === 'multiple_choice'
              ? true
              : node.awaitButtonNext ?? false,
          awaitAudioEndBeforeInput: node.allowedKeys?.length ? true : false,
          advanceOnAudioEnd: advanceOnAudioEndFallback
            ? advanceOnAudioEndFallback
            : node.advanceOnAudioEnd,
          delayBeforeAdvancingMs: node.delayBeforeAdvancingMs ?? 0,
          enableClickAnywhere: node.enableClickAnywhere ?? false,
          allowedKeys: node.allowedKeys || [],
          cbOnCompletedStep: () => {},
          questionary: node.questionary,
          text: node.text,
        };
      });

      nodes.push({
        id: `step-${nodes.length + 1}`,
        play: async () => {
          linearScriptPlayerRef.current?.complete();
          setIsScriptCompleted(true);
        },
      });

      log('build nodes', nodes);

      log('preRenderedAudio ' + currentArea, preRenderedAudioRef.current);

      setIsScriptCompleted(false);
      linearScriptPlayerRef.current = new LinearScriptPlayer(nodes, {
        name: currentArea,
        onComplete: () => setIsScriptCompleted(true),
      });
      let step = 1;

      if (
        !bypassPlaybackPolicy &&
        playbackPolicy === PlaybackPolicy.ONCE_USER &&
        currentProgressState?.[currentArea]
      ) {
        step = (currentProgressState?.[currentArea]?.at(-1)?.[0] ?? -1) + 2;
      }

      if (startStep !== undefined) {
        step = startStep;
      }

      log(
        `[progress] startFlow starting player at step-${step}, progressState:`,
        progressState.current,
      );

      if (isUiClick) {
        if (!openRef.current) {
          setOpen(true);
        }
        setPendingScriptStart({
          step,
          area: currentArea,
          // Unlike script steps, the button is never gated on translation
          // completeness: a language without its own label silently falls
          // back to the base English "Take a tour" label.
          title:
            script?.uiClickButtonLabelTranslations?.[langKey] ||
            script?.uiClickButtonLabel ||
            script?.startScriptButtonTitle ||
            'Take a tour',
        });
      } else {
        linearScriptPlayerRef.current.start(`step-${step}`);
        startAudioPrerenderRef.current(currentArea, step);
      }
    },
    [resetAssistantUIRef],
  );
  startFlowRef.current = startFlow;

  const confirmScriptStart = useCallback(() => {
    if (!pendingScriptStart) {
      return;
    }
    const { step, area } = pendingScriptStart;
    setPendingScriptStart(null);
    linearScriptPlayerRef.current?.start(`step-${step}`);
    startAudioPrerenderRef.current(area, step);
  }, [pendingScriptStart]);

  return {
    isScriptCompleted,
    setIsScriptCompleted,
    isScriptTransitioning,
    setIsScriptTransitioning,
    canUserSkip,
    setCanUserSkip,
    pendingScriptStart,
    setPendingScriptStart,
    isScriptQuizActive,
    setIsScriptQuizActive,
    startFlow,
    confirmScriptStart,
  };
};
